import { hasPermission } from 'core/auth/rbac'
import { ConfirmDialog } from 'core/components/ui/ConfirmDialog'
import { FilterBar } from 'core/components/ui/FilterBar'
import { Modal } from 'core/components/ui/Modal'
import { PageHeader } from 'core/components/ui/PageHeader'
import { useAuth } from 'features/auth/presentation/context/AuthContext'
import React, { useState } from 'react'
import { KycListItem } from '../../domain/entities/Kyc'
import { KycStatusFilter } from '../../domain/repositories/KycRepository'
import { KycDetailModal } from '../components/KycDetailModal'
import { KycQueue } from '../components/KycQueue'
import { useKyc } from '../hooks/useKyc'

export function KycVerificationPage() {
  const { user } = useAuth()
  const canVerify = hasPermission(user, 'approve_kyc')

  const {
    records,
    loading,
    error,
    status,
    setStatus,
    page,
    setPage,
    totalPages,
    totalItems,
    approveKyc,
    rejectKyc,
    resetFilters,
  } = useKyc(10)

  const [selectedRecord, setSelectedRecord] = useState<KycListItem | null>(null)
  const [detailOpen, setDetailOpen] = useState(false)

  // Verification dialogs
  const [approveOpen, setApproveOpen] = useState(false)
  const [rejectOpen, setRejectOpen] = useState(false)
  const [rejectReason, setRejectReason] = useState('')
  const [actioning, setActioning] = useState(false)

  const handleReviewClick = (record: KycListItem) => {
    setSelectedRecord(record)
    setDetailOpen(true)
  }

  const handleApproveTrigger = () => {
    setDetailOpen(false)
    setApproveOpen(true)
  }

  const handleRejectTrigger = () => {
    setDetailOpen(false)
    setRejectReason('')
    setRejectOpen(true)
  }

  const handleApproveConfirm = async () => {
    if (!selectedRecord) return
    setActioning(true)
    try {
      await approveKyc(selectedRecord.id)
      setApproveOpen(false)
      setSelectedRecord(null)
    } catch (err) {
      // Handled by hook error state
    } finally {
      setActioning(false)
    }
  }

  const handleRejectConfirm = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!selectedRecord || !rejectReason) return
    setActioning(true)
    try {
      await rejectKyc(selectedRecord.id, rejectReason)
      setRejectOpen(false)
      setSelectedRecord(null)
    } catch (err) {
      // Handled
    } finally {
      setActioning(false)
    }
  }

  const isFilterActive = status !== 'all'

  const rejectModalFooter = (
    <>
      <button
        type="button"
        onClick={() => setRejectOpen(false)}
        disabled={actioning}
        className="px-4 py-2 text-sm font-semibold border border-border rounded-btn bg-surface hover:bg-bg text-text-secondary hover:text-text-primary transition-colors disabled:opacity-50"
      >
        Cancel
      </button>
      <button
        type="submit"
        form="reject-kyc-form"
        disabled={actioning || !rejectReason}
        className="px-4 py-2 text-sm font-semibold bg-danger hover:bg-danger-hover text-white rounded-btn transition-colors disabled:opacity-50 min-w-[5rem] flex items-center justify-center"
      >
        {actioning ? (
          <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        ) : (
          'Confirm Reject'
        )}
      </button>
    </>
  )

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <PageHeader
        title="Partner KYC"
        description="Verify retailer identity profiles, PAN registrations, and GSTIN documentation to unlock order indents."
        breadcrumbs={[{ label: 'Portal', path: '/' }, { label: 'KYC Queue' }]}
      />

      {/* Fetch error banner */}
      {error && (
        <div className="p-3 text-sm bg-danger/10 border border-danger/20 text-danger rounded-btn font-medium">
          {error}
        </div>
      )}

      {/* Filter Bar */}
      <FilterBar showReset={isFilterActive} onReset={resetFilters}>
        {/* Status filter */}
        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as KycStatusFilter)
            setPage(1)
          }}
          className="px-3 py-2 border border-border rounded-btn bg-surface text-text-primary text-xs font-semibold focus:outline-none"
        >
          <option value="all">All Records</option>
          <option value="pending">Pending KYC</option>
          <option value="verified">Verified KYC</option>
          <option value="rejected">Rejected KYC</option>
        </select>
      </FilterBar>

      {/* Data Table queue */}
      <KycQueue
        records={records}
        loading={loading}
        onReview={handleReviewClick}
        currentPage={page}
        totalPages={totalPages}
        onPageChange={setPage}
        totalItems={totalItems}
        itemsPerPage={10}
      />

      {/* Document Review Modal */}
      <KycDetailModal
        isOpen={detailOpen}
        onClose={() => setDetailOpen(false)}
        record={selectedRecord}
        onApprove={handleApproveTrigger}
        onReject={handleRejectTrigger}
        readOnly={!!selectedRecord?.verified || !!selectedRecord?.rejected || !canVerify}
      />

      {/* Approval Confirm Dialog */}
      <ConfirmDialog
        isOpen={approveOpen}
        onClose={() => setApproveOpen(false)}
        onConfirm={handleApproveConfirm}
        title="Approve Retailer KYC"
        message={`Are you sure you want to verify and approve KYC registration for ${
          selectedRecord?.businessName || 'this user'
        }? This will allow them to start submitting cattle feed indents immediately.`}
        confirmText="Verify & Approve"
        tone="primary"
        loading={actioning}
      />

      {/* Rejection Form Modal */}
      <Modal
        isOpen={rejectOpen}
        onClose={() => setRejectOpen(false)}
        title="Reject Retailer KYC"
        footer={rejectModalFooter}
        size="sm"
      >
        <form id="reject-kyc-form" onSubmit={handleRejectConfirm} className="space-y-4">
          <div className="space-y-2">
            <p className="text-text-secondary text-sm">
              Please enter the reason for rejecting KYC documents. This note will be sent directly to the retailer.
            </p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g. Uploaded PAN image is blurred / GSTIN status is inactive..."
              required
              rows={3}
              className="w-full px-3 py-2 border border-border rounded-btn bg-bg text-text-primary text-sm focus:border-danger focus:outline-none transition-colors"
            />
          </div>
        </form>
      </Modal>
    </div>
  )
}
